import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Table } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { Link, Loader, Pagination } from 'components';
import { IRootState } from 'config/redux';
import { RouteConstants, ServiceConstants } from 'constant';
import { useListHook } from 'hooks';
import { UserService } from 'services';
import { tableClasses } from 'styles';
import { UserResponse } from 'types';
import { AppUtils } from 'utils';

const { REQUEST_PENDING, REQUEST_USERS_LIST_ID } = ServiceConstants;

const AgentListTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const serviceObject = useSelector((state: IRootState) => state.service.object[REQUEST_USERS_LIST_ID]);
  const { content, page, totalPages, setPage, updatePageable, resetList } = useListHook();

  // Initialize page to 0 on mount
  useEffect(() => {
    resetList();
  }, [resetList]);

  // Fetch data when page changes
  useEffect(() => {
    if (page === undefined || page === -1) return;
    UserService.usersAll({ page, search: '', pageSize: 20, type: 'EMPLOYEE' });
  }, [page]);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, REQUEST_USERS_LIST_ID, updatePageable);
  }, [serviceObject, updatePageable]);

  const isLoading = useCallback(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject]);

  const handleRowClick = useCallback((agentId: number) => {
    navigate(`${RouteConstants.ROUTE_PERMISSIONS}/agenti/${agentId}`);
  }, [navigate]);

  const rows = useMemo(() => {
    if (!content) return null;
    return content.map((agent: UserResponse, index: number) => {
      const rowBg = index % 2 === 1 ? 'var(--mantine-color-gray-1)' : 'var(--mantine-color-gray-0)';
      // Check if agent has deviations (custom authorities different from profile)
      // For now, we don't have this info from API, so we'll show profile name only
      const profileName = agent.profile?.name || '-';
      return (
        <Table.Tr
          key={agent.id}
          onClick={() => handleRowClick(agent.id)}
          style={{ cursor: 'pointer' }}
        >
          <Table.Td style={{ backgroundColor: rowBg }}>
            <Link
              to={`${RouteConstants.ROUTE_PERMISSIONS}/agenti/${agent.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              {agent.fullName}
            </Link>
          </Table.Td>
          <Table.Td style={{ backgroundColor: rowBg }}>
            {profileName}
          </Table.Td>
        </Table.Tr>
      );
    });
  }, [content, handleRowClick]);

  return (
    <Box pos="relative">
      <Loader overlay={true} visible={content === undefined || isLoading()} />
      <Table className={tableClasses.table}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: '15%' }}>{t('permissions.agents.name')}</Table.Th>
            <Table.Th>{t('permissions.agents.templateType')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>
      <Pagination value={page} onChange={setPage} total={totalPages} />
    </Box>
  );
};

export default AgentListTab;
